import React, { forwardRef, useState, useEffect } from "react";
import { Spinner } from "reactstrap";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import L from "leaflet";
import Control from "react-leaflet-control";
import PropTypes from "prop-types";
import {
  setHollowMaps,
  setSigTipeData,
  setDirectionToggle,
  resetBidangHierarki,
} from "~/redux/actions";
import axios from "axios";
import fileDownload from "js-file-download";
import { replaceToFullPath } from "~/utils";
import request from "~/utils/request";
import "leaflet-control-geocoder";

const HomeControl = forwardRef(
  (
    {
      position,
      pembangunan,
      deleteData,
      handleDeleteClick,
      disabledDelete,
      download,
      handleDownloadPeta,
      editStyleLayers,
      handleEditStyleLayers,
      hollowMaps,
      dynamicDownloadMaps,
    },
    ref
  ) => {
    const { desa } = useSelector((state) => state.Auth);
    const {
      directionToggle,
      hollowMaps: hollowMapsReducers,
      sigTipeData,
    } = useSelector((state) => state.Map);

    const [loadingDownloadMaps, setLoadingDownloadMaps] = useState(false);

    const { search: searchQueryParams, pathname } = useLocation();

    const tipeQuery = new URLSearchParams(searchQueryParams).get("tipe");

    const dispatch = useDispatch();

    const handleZoomIn = () => ref.current.leafletElement.zoomIn();

    const handleZoomOut = () => ref.current.leafletElement.zoomOut();

    const handleHome = () => {
      ref.current.leafletElement.flyTo(
        new L.LatLng(desa?.center?.[0], desa?.center?.[1]),
        15
      );

      dispatch(resetBidangHierarki(true));
    };

    const circleClick = (e) => {
      const clickedCirlce = e.target;
      clickedCirlce.bindPopup("Posisi anda disini !").openPopup();
    };

    const handleCurrentLocation = () => {
      const map = ref.current?.leafletElement;

      map
        .locate({
          watch: true,
          setView: true,
          maxZoom: 16,
        })
        .on("locationfound", (e) => {
          map.flyTo(e.latlng, map?.getZoom());
          const radius = e.accuracy;
          const circle = L.circleMarker(e.latlng, radius);

          circle.addTo(map).on("click", circleClick);
        });
    };

    const handleDynamicDownloadMaps = () => {
      setLoadingDownloadMaps(true);

      request
        .get(`/v1/sigtipe/${sigTipeData?.id}/download`)
        .then((res) => {
          const downloadURL = replaceToFullPath(res?.data?.url);

          axios
            .get(downloadURL, {
              responseType: "blob",
            })
            .then((response) => {
              const file = response.data;

              fileDownload(file, `peta-${sigTipeData?.nama}.geojson`);
            });
        })
        .finally(() => setLoadingDownloadMaps(false));
    };

    useEffect(() => {
      if (dynamicDownloadMaps) {
        const uri =
          pathname === "/peta-bidang"
            ? `/v1/sigtipe?filter{nama.contains}=bidang`
            : `/v1/sigtipe?filter{nama.contains}=${tipeQuery}`;

        request.get(uri).then((res) => {
          request
            .get(`/v1/sigtipe/${res?.data?.data?.[0]?.id}`)
            .then((response) => dispatch(setSigTipeData(response?.data?.data)));
        });
      }
    }, []);

    return (
      <>
        <Control
          className={`leaflet-control-zoom leaflet-bar leaflet-control ${
            pembangunan && "mr-3"
          }`}
          position={position}
        >
          <button
            type="button"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Perbesar"
            onClick={handleZoomIn}
            className="button-home leaflet-control-zoom-out"
          >
            <i className="mdi mdi-plus text-grey" />
          </button>

          <button
            type="button"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Kembali ke titik awal"
            onClick={handleHome}
            className="button-home leaflet-control-zoom-out"
          >
            <i className="mdi mdi-home text-grey" />
          </button>

          <button
            type="button"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Perkecil"
            onClick={handleZoomOut}
            className="button-home leaflet-control-zoom-out"
          >
            <i className="mdi mdi-minus text-grey" />
          </button>
        </Control>
        <Control
          className={`leaflet-control-zoom leaflet-bar leaflet-control ${
            pembangunan && "mr-3"
          }`}
          position={position}
        >
          <button
            type="button"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Lokasi saya"
            onClick={handleCurrentLocation}
            className="button-home leaflet-control-zoom-out"
          >
            <i
              className="mdi mdi-crosshairs-gps text-grey"
              style={{ fontSize: "1.2rem", marginRight: ".1rem" }}
            />
          </button>
        </Control>

        <Control
          className={`leaflet-control-zoom leaflet-bar leaflet-control ${
            pembangunan && "mr-3"
          }`}
          position={position}
        >
          <button
            type="button"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Ukur jalan"
            onClick={() => dispatch(setDirectionToggle(!directionToggle))}
            className="button-home leaflet-control-zoom-out"
          >
            <i
              className="mdi mdi-directions text-grey"
              style={{ fontSize: "1.2rem", marginRight: ".1rem" }}
            />
          </button>
        </Control>

        {deleteData && (
          <Control
            className="leaflet-control-zoom leaflet-bar leaflet-control"
            position={position}
          >
            <button
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Hapus data sebelumnya"
              type="button"
              disabled={disabledDelete}
              onClick={handleDeleteClick}
              className="button-home leaflet-control-zoom-out"
            >
              <i
                className="mdi mdi-delete-outline text-grey"
                style={{ fontSize: "1.2rem", marginRight: ".1rem" }}
              />
            </button>
          </Control>
        )}
        {download && (
          <Control
            className="leaflet-control-zoom leaflet-bar leaflet-control"
            position={position}
          >
            <button
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Download Peta"
              type="button"
              disabled={loadingDownloadMaps}
              className="button-home leaflet-control-zoom-out"
              onClick={
                dynamicDownloadMaps
                  ? handleDynamicDownloadMaps
                  : handleDownloadPeta
              }
            >
              {loadingDownloadMaps ? (
                <Spinner size="sm" />
              ) : (
                <i
                  className="mdi mdi-download text-grey"
                  style={{ fontSize: "1.2rem", marginRight: ".1rem" }}
                />
              )}
            </button>
          </Control>
        )}

        {hollowMaps && (
          <Control
            className="leaflet-control-zoom leaflet-bar leaflet-control"
            position={position}
          >
            <button
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Hollow Maps"
              type="button"
              onClick={() => dispatch(setHollowMaps(!hollowMapsReducers))}
              className={`button-home ${
                hollowMapsReducers && "bg-primary border-0"
              } leaflet-control-zoom-out`}
            >
              <i
                style={{ fontSize: "1.2rem", marginRight: ".1rem" }}
                className={`mdi mdi-brush ${
                  hollowMapsReducers ? "text-white" : "text-grey"
                }`}
              />
            </button>
          </Control>
        )}

        {editStyleLayers && (
          <Control
            className="leaflet-control-zoom leaflet-bar leaflet-control"
            position={position}
          >
            <button
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Edit Style Layer"
              type="button"
              onClick={handleEditStyleLayers}
              className="button-home leaflet-control-zoom-out"
            >
              <i
                className="mdi mdi-pencil-plus text-grey"
                style={{ fontSize: "1.2rem", marginRight: ".1rem" }}
              />
            </button>
          </Control>
        )}
      </>
    );
  }
);

HomeControl.propTypes = {
  position: PropTypes.string,
  pembangunan: PropTypes.bool,
  deleteData: PropTypes.bool,
  handleDeleteClick: PropTypes.func,
  disabledDelete: PropTypes.bool,
  download: PropTypes.bool,
  handleDownloadPeta: PropTypes.func,
  editStyleLayers: PropTypes.bool,
  handleEditStyleLayers: PropTypes.func,
  handleHomeCallback: PropTypes.func,
  dynamicDownloadMaps: PropTypes.bool,
};

export default HomeControl;
